<template>
  <div :class="layoutClass">
    <div :class="['flex justify-center', responsive ? '' : 'my-4']">
      <div class="p-4 bg-white rounded-xl shadow-sm border border-gray-200">
        <img
          v-if="qrSource"
          :src="qrSource"
          alt="QR Code"
          class="w-64 h-64 object-contain"
        >
        <div
          v-else
          class="w-64 h-64 flex items-center justify-center bg-gray-100 dark:bg-gray-800 text-gray-400"
        >
          <base-loader :size="24" />
        </div>
      </div>
    </div>

    <div class="text-left bg-gray-50 dark:bg-gray-900 p-4 rounded-xl space-y-2 text-sm border border-gray-100 dark:border-gray-800">
      <div class="flex justify-between">
        <span class="text-gray-500">{{ $t('REGISTER.BANK_NAME') }}:</span>
        <span class="font-semibold text-gray-950 dark:text-white">{{ paymentInfo?.bank }}</span>
      </div>
      <div class="flex justify-between">
        <span class="text-gray-500">{{ $t('REGISTER.ACCOUNT_NUMBER') }}:</span>
        <span class="font-mono font-semibold text-gray-950 dark:text-white">{{ paymentInfo?.accountNumber }}</span>
      </div>
      <div class="flex justify-between">
        <span class="text-gray-500">{{ $t('REGISTER.ACCOUNT_HOLDER') }}:</span>
        <span class="font-semibold text-gray-950 dark:text-white">{{ paymentInfo?.accountHolder }}</span>
      </div>
      <div
        v-if="packageName"
        class="flex justify-between gap-4"
      >
        <span class="text-gray-500">{{ $t('REGISTER.CREDIT_PACKAGE') }}:</span>
        <span class="text-right font-semibold text-gray-950 dark:text-white">{{ packageName }}</span>
      </div>
      <div
        v-if="credits"
        class="flex justify-between"
      >
        <span class="text-gray-500">{{ $t('REGISTER.CREDITS') }}:</span>
        <span class="font-semibold text-gray-950 dark:text-white">{{ credits }}</span>
      </div>
      <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2 text-[21px] font-bold leading-tight">
        <span class="text-gray-900 dark:text-white">{{ $t('REGISTER.AMOUNT') }}:</span>
        <span class="text-primary-500">{{ formatPrice(amount || 0) }}</span>
      </div>
      <div class="flex justify-between border-t pt-2 mt-2 border-gray-200 dark:border-gray-800">
        <span class="text-gray-500">{{ $t('REGISTER.TRANSFER_CONTENT') }}:</span>
        <span class="font-mono font-bold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-2 py-0.5 rounded">
          {{ paymentInfo?.transferDescription }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import QRCode from "qrcode";
import {
  formatPrice,
} from "~/utils/formatters";

type PaymentInfo = {
  /**
   * VietQR payload rendered in the browser. Preferred over qrUrl, because
   * SePay's image endpoint answers embedded requests with a bot challenge.
   */
  qrPayload?: string;
  qrUrl?: string;
  bank?: string;
  accountNumber?: string;
  accountHolder?: string;
  transferDescription?: string;
};

const props = defineProps<{
  paymentInfo: PaymentInfo | null;
  amount: number;
  packageName?: string;
  credits?: number;
  responsive?: boolean;
  stackOnLarge?: boolean;
}>();

const renderedQr = ref<string | null>(null);

watch(() => props.paymentInfo?.qrPayload, async (payload) => {
  if (!payload) {
    renderedQr.value = null;
    return;
  }
  try {
    renderedQr.value = await QRCode.toDataURL(payload, {width: 512, margin: 1});
  } catch (error) {
    console.warn("[PaymentInfoDisplay] Failed to render the VietQR code:", error);
    renderedQr.value = null;
  }
}, {immediate: true});

// Falls back to SePay's hosted image so nothing breaks for orders created
// before the payload was introduced.
const qrSource = computed(() => renderedQr.value ?? props.paymentInfo?.qrUrl ?? null);

const layoutClass = computed(() => {
  if (!props.responsive) {
    return "space-y-6";
  }
  if (props.stackOnLarge) {
    return "grid grid-cols-1 gap-6 items-center md:grid-cols-[auto_minmax(0,1fr)] lg:grid-cols-1";
  }
  return "grid grid-cols-1 gap-6 items-center sm:grid-cols-[auto_minmax(0,1fr)]";
});
</script>
